<template>
    <div class="audio-player-compact" v-if="audioData">
        <div class="audio-buttons-compact" dir="ltr">
            <ion-icon id="rewind-button-compact" @click="seek(-10)" src="/assets/img/audioRewind10.svg" class="audio-button-compact icon" />
            <ion-icon v-if="!isPlaying" @click="togglePlay" id="play-button-compact" src="/assets/img/audioPlay.svg" class=" audio-button-compact-lg icon"/>
            <ion-icon v-else @click="togglePlay" id="pause-button-compact" src="/assets/img/audioPause.svg" class=" audio-button-compact-lg icon" />
            <ion-icon id="forward-button-compact" @click="seek(10)" src="/assets/img/audioForward10.svg" class="audio-button-compact icon" />
        </div>
        
            <ion-range 
                ref="seekBar" 
                v-model="seekBarValue" 
                @ionChange="rangeChange" 
                @mousedown="seekBarTouchStart"
                @mouseup="seekBarTouchEnd"
                @touchstart="seekBarTouchStart"
                @touchend="seekBarTouchEnd"
                :style="{'--bar-background-active': 'var('+color+')' }" 
                class="seek-bar-compact" 
                :class="[{'enable-transitions': enableTransition}, direction]" 
                min="0" 
                max="100" 
                pin="false">
            </ion-range>
        
    </div>
</template>

<script>
import { defineComponent, ref, computed, watch } from 'vue';
import { IonRange} from '@ionic/vue';
import { useStore } from 'vuex';
import { getAudioDataUri } from "@/utilities/Compatibility";
import translate from "@/views/translate";
import { KeepAwake } from '@capacitor-community/keep-awake';
import { Device } from '@capacitor/device';
import { logInfo } from '@/utilities/Filesystem';

export default defineComponent({
    name: 'AudioPlayer',
    props: {
        color: { type: String, default: 'var(--ion-color-primary)' },
        startTimeSeconds: { type: Number, default: 0 },
        endTimeSeconds: { type: Number, default: 0 },
        audioData: { type: String, defaults: '' }
    },
    components: { IonRange  },
    setup(props, { emit }) {
        const store = useStore();
        const { t } = translate();
        const isPlaying = ref(false);
        const smartEndTimeSeconds = ref(0);
        const audio = ref(new Audio());
        const direction = computed(() => t("messages.language.textDirection"));
        audio.value.ondurationchange = (e) => {
            // Our calculated endTimeSeconds could be slightly higher than the actual length of the audio file due to small margins
            // of error as part of merging mp3 files. Set the max to be the actual length of the audio file
            smartEndTimeSeconds.value = !props.endTimeSeconds || props.endTimeSeconds > e.target.duration ? e.target.duration : props.endTimeSeconds;
            emit('audioLoaded');
        };

        const audioSrc = computed(() => getAudioDataUri(props.audioData));
        audio.value.src = audioSrc.value;
        audio.value.preload = 'metadata'
        const seekBarValue = ref(0);
        const enableTransition = ref(true);
        const audioTime = ref("0:00");
        let seekbarInterval; 
        let seekbarIsHeld = false;
        
        
        function pause() {
            isPlaying.value = false;
            clearInterval(seekbarInterval);
            audio.value.pause();
        }

        watch( () => [props.audioData, props.startTimeSeconds, props.endTimeSeconds], () => {
             audio.value.src = getAudioDataUri(props.audioData);
        });
        
        watch(() => store.state.shouldPauseAudio, (newValue) => {
           if (newValue)
               pause();
        });

        // Watch isPlaying to handle keep-awake for Android
        watch(isPlaying, async (newValue) => {
            try {
                const info = await Device.getInfo();
                if (info.platform !== 'web'){
                    if (newValue) {
                        await KeepAwake.keepAwake();
                        //console.log('Keep awake enabled for audio playback');
                    } else {
                        await KeepAwake.allowSleep();
                        //console.log('Keep awake disabled');
                    }
                }
            } catch (error) {
                logInfo(`Error with KeepAwake plugin: ${error}`);
            }
        });

        function formatSeconds (seconds) {
            seconds = seconds < 0 ? 0 : seconds;
            const minutes = Math.floor(seconds / 60);
            let justSeconds = Math.round(seconds % 60);
            justSeconds = justSeconds < 10 ? "0" + justSeconds : justSeconds;
            return minutes === Infinity ? '' : minutes + ":" + justSeconds;
        }

        function evaluateSeekBarValue() {
            const timePosition = audio.value.currentTime - props.startTimeSeconds;
            const endPosition = smartEndTimeSeconds.value - props.startTimeSeconds;
            const seekBarPosition = (timePosition / endPosition) * 100;
            if (seekBarPosition >= 100) {
                pause();
            }
            audioTime.value = formatSeconds(timePosition);
            seekBarValue.value = seekBarPosition;
        }
        
        function play() {
            store.dispatch("setShouldPauseAudio", false);
            isPlaying.value = true;
            if(audio.value.currentTime >= smartEndTimeSeconds.value){
                audio.value.currentTime = props.startTimeSeconds;
            }
            audio.value.play();
            evaluateSeekBarValue();
            clearInterval(seekbarInterval);
            seekbarInterval = setInterval( () => {
                evaluateSeekBarValue();
            }, 100);
        }
        
        function togglePlay() {
            isPlaying.value = !isPlaying.value;
            if(isPlaying.value)
                play();
            else pause();
        }

        audio.value.addEventListener("loadedmetadata", function(event) {
            audio.value.currentTime = props.startTimeSeconds;
            if (!props.endTimeSeconds && !smartEndTimeSeconds.value) {
                if (audio.value.duration === Infinity) {
                    try {
                        audio.value.duration = 99999999; //necessary for macOS/iOS
                        audio.value.ontimeupdate = () => {
                            audio.value.ontimeupdate = null;
                            audio.value.currentTime = props.startTimeSeconds;
                            smartEndTimeSeconds.value = event.target.duration;
                        };
                    }
                    catch {
                        // above not valid in Firefox
                    }
                }
                else {
                    //smartEndTimeSeconds.value = event.target.duration;
                }
            } 
            else if (props.endTimeSeconds) {
                //smartEndTimeSeconds.value = props.endTimeSeconds;
            }
            setTimeout( () => {
                pause();
                audioTime.value = "0:00";
                seekBarValue.value = 0;
            }, 0);
        });
        
        function seek (seconds) {
            const proposedSeekTime = Math.round(audio.value.currentTime + seconds);
            if (proposedSeekTime >= smartEndTimeSeconds.value && smartEndTimeSeconds.value){
                audio.value.currentTime = smartEndTimeSeconds.value;
                pause();
            }
            else if (proposedSeekTime <= props.startTimeSeconds){
                audio.value.currentTime = props.startTimeSeconds;
            }
            else {
                audio.value.currentTime = proposedSeekTime;
            }
            evaluateSeekBarValue();
            if(audio.value.currentTime >= smartEndTimeSeconds.value && smartEndTimeSeconds.value){
                pause();
            }
        }

        function seekBarTouchStart () {    
            clearInterval(seekbarInterval);
            seekbarIsHeld = true;
        }

        function getAudioFileTime () {
            const audioLength = smartEndTimeSeconds.value - props.startTimeSeconds;
            const seekBarPercentage = seekBarValue.value / 100;
            const startTime = +props.startTimeSeconds;
            return (audioLength * seekBarPercentage) + startTime;
        }

        function seekBarTouchEnd () {
            const waitForRangeChange = () => {
                seekbarIsHeld = false;
                const timeToSet = getAudioFileTime();
                if (timeToSet >= smartEndTimeSeconds.value && smartEndTimeSeconds.value) {
                    audio.value.currentTime = smartEndTimeSeconds.value;
                    pause();
                } else {
                    audio.value.currentTime = timeToSet;
                    play();
                }
            }
            setTimeout(waitForRangeChange, 200);
        }

        function getAudioTimeForTab () {
            const audioLength = smartEndTimeSeconds.value - props.startTimeSeconds;
            const seekBarPercentage = seekBarValue.value / 100;
            return (audioLength * seekBarPercentage);
        }

        function rangeChange(){
            if(seekbarIsHeld){
                pause();
                audioTime.value = formatSeconds(getAudioTimeForTab());
            }
        }

        return {
            isPlaying,
            seekBarValue,
            seek,
            enableTransition,
            enableTransitionTimeout: null, 
            togglePlay,
            seekBarTouchStart,
            seekBarTouchEnd, 
            formatSeconds,
            audioTime,
            rangeChange,
            smartEndTimeSeconds,
            startTime: computed(() => formatSeconds(props.startTimeSeconds)),
            endTime: computed(() => formatSeconds(smartEndTimeSeconds.value - props.startTimeSeconds)),
            direction
        };
    }
});
</script>

<style scoped>
.audio-player-compact {
    display: flex;
    flex-direction: row;
    /* margin:  15px; */
    border-radius:10px;
    padding: 10px 25px 10px 25px;
    background-color: var(--color-card-background);
    box-shadow: 0px 1px 2px  var(--color-primary);
}


.audio-buttons-compact {
    display: flex;
    gap:5px;
    align-items: center;
    cursor: pointer;
}
.audio-button-compact {
    font-size: 24px;
    color:var(--color-primary);
}

#play-button-compact, #pause-button-compact {
    margin: 0 20px;
    font-size: 36px;
    color:var(--color-primary)
}

.seek-bar-compact.ltr {
    margin: 0 0px 0 20px;
}

.seek-bar-compact.rtl {
    margin: 0 20px 0 0px;
}

.audio-player {
    display: flex;
    flex-direction: column;
    margin: 0 35px;
    padding-top: 16px;
    padding-bottom: 20px;
}

.enable-transitions {
    --transition: .1s linear;
}

.seek-time {
    font-size: 12px;
    font-weight: 600;
    color: var(--color-text-gray);
    margin-left: 5px;
    text-align: start;
}

.audio-buttons {
    display: flex;
    justify-content: center;
    align-items: center;
    fill: var(--ion-color-primary);
    cursor: pointer;
}

#play-button, #pause-button {
    margin: 0 56px;
}

ion-range::part(knob) {
    display: none;
}

ion-range::part(range-slider) {
}

ion-range::part(bar-active) {
    transition: var(--transition);
}

ion-range {
    --height: 8px;
    --bar-height: 8px;
    --bar-border-radius: 10px;
    --bar-background: var(--ion-color-custom-step-800);
    --bar-background-active: var(--ion-color-primary);
    padding: 0;
    margin-bottom: 4px;
    
}
</style>
